import { apiService } from '@/services/ApiService';
import { SettingsFieldConfig } from '@/types/model/settingsModal';
import { EnterprisePreferencesResponse, LicenseInfoResponse, OfflineAppSettingsResponse } from '@/types/response/enterpriseResponses';

// ---- Email Settings ----

export interface EmailSettingsForm {
    emailMode: 'seesor' | 'smtp';
    server: string;
    port: string;
    user: string;
    changePassword: boolean;
    password: string;
}

export const emailSettingsEmptyForm: EmailSettingsForm = {
    emailMode: 'seesor', server: '', port: '', user: '', changePassword: false, password: '',
};

export const emailSettingsInitialData = (p: EnterprisePreferencesResponse | null): EmailSettingsForm | null =>
    p ? {
        emailMode: p.UseCustomEmailServer ? 'smtp' : 'seesor',
        server: p.EmailServer ?? '',
        port: p.EmailPort?.toString() ?? '',
        user: p.EmailUser ?? '',
        changePassword: false,
        password: '',
    } : null;

export const emailSettingsFields: SettingsFieldConfig<EmailSettingsForm>[] = [
    {
        key: 'emailMode', label: 'Email Support', type: 'radio',
        options: [{ value: 'seesor', label: 'SeeSOR' }, { value: 'smtp', label: 'SMTP' }],
    },
    {
        key: 'server', label: 'Server', type: 'text', visible: form => form.emailMode === 'smtp',
        validate: (v, form) => (form.emailMode === 'smtp' && !String(v).trim() ? 'Server is required.' : undefined),
    },
    {
        key: 'port', label: 'Port', type: 'number', visible: form => form.emailMode === 'smtp',
        validate: (v, form) => {
            if (form.emailMode !== 'smtp') return undefined;
            if (!String(v).trim()) return 'Port is required.';
            if (isNaN(parseInt(String(v), 10))) return 'Port must be a number.';
            return undefined;
        },
    },
    {
        key: 'user', label: 'User', type: 'text', visible: form => form.emailMode === 'smtp',
        validate: (v, form) => (form.emailMode === 'smtp' && !String(v).trim() ? 'User is required.' : undefined),
    },
    { key: 'changePassword', label: 'Change SMTP Password', type: 'checkbox', visible: form => form.emailMode === 'smtp' },
    {
        key: 'password', label: 'New Password', type: 'text', visible: form => form.emailMode === 'smtp' && form.changePassword,
        validate: (v, form) => (form.emailMode === 'smtp' && form.changePassword && !String(v) ? 'Password is required.' : undefined),
    },
];

export const saveEmailSettings = async (form: EmailSettingsForm): Promise<void> => {
    const useCustom = form.emailMode === 'smtp';
    await apiService.saveEmailPreferences({
        UseCustomEmailServer: useCustom,
        EmailServer: useCustom ? form.server : undefined,
        EmailPort: useCustom ? form.port : undefined,
        EmailUser: useCustom ? form.user : undefined,
        EmailPassword: useCustom && form.changePassword ? form.password : undefined,
    });
};

// ---- Password & Sign In Parameters ----

export interface PasswordSignInForm {
    PasswordLength: string;
    PasswordSpecialCharacters: string;
    PasswordNumericCharacters: string;
    PasswordUpperCaseCharacters: string;
    PasswordExpirationDays: string;
    PasswordGenerations: string;
    InvalidSignInAttempts: string;
    InvalidSignInMinutes: string;
    AutoResetMinutes: string;
    GenerateIdleWarningMinutes: string;
    IdleWarningDurationMinutes: string;
}

const PASSWORD_SIGN_IN_LABELS: { key: keyof PasswordSignInForm; label: string }[] = [
    { key: 'PasswordLength', label: 'Minimum Password Length' },
    { key: 'PasswordSpecialCharacters', label: 'Minimum Special Characters' },
    { key: 'PasswordNumericCharacters', label: 'Minimum Numeric Characters' },
    { key: 'PasswordUpperCaseCharacters', label: 'Minimum Upper Case Characters' },
    { key: 'PasswordExpirationDays', label: 'Password Expiration Days' },
    { key: 'PasswordGenerations', label: 'Min Generations for Password Reuse Prohibition' },
    { key: 'InvalidSignInAttempts', label: 'Max Consecutive Invalid Sign In Attempts' },
    { key: 'InvalidSignInMinutes', label: 'During Number of Minutes' },
    { key: 'AutoResetMinutes', label: 'Auto-reset Sign In after Minutes' },
    { key: 'GenerateIdleWarningMinutes', label: 'Generate Idle Warning after Minutes' },
    { key: 'IdleWarningDurationMinutes', label: 'Minutes of Idle Warning before Auto Log Out' },
];

export const passwordSignInEmptyForm: PasswordSignInForm = PASSWORD_SIGN_IN_LABELS.reduce(
    (acc, { key }) => ({ ...acc, [key]: '' }), {} as PasswordSignInForm,
);

export const passwordSignInInitialData = (p: EnterprisePreferencesResponse | null): PasswordSignInForm | null =>
    p ? PASSWORD_SIGN_IN_LABELS.reduce((acc, { key }) => ({ ...acc, [key]: p[key]?.toString() ?? '' }), {} as PasswordSignInForm) : null;

export const passwordSignInFields: SettingsFieldConfig<PasswordSignInForm>[] = PASSWORD_SIGN_IN_LABELS.map(({ key, label }) => ({
    key, label, type: 'number',
    validate: v => {
        const trimmed = String(v).trim();
        if (trimmed === '') return undefined;
        const n = parseInt(trimmed, 10);
        if (key === 'PasswordLength') return isNaN(n) || n < 1 ? 'Minimum Password Length must be at least 1 if entered.' : undefined;
        return isNaN(n) || n < 0 ? `${label} must be a valid non-negative number.` : undefined;
    },
}));

export const savePasswordSignIn = async (form: PasswordSignInForm): Promise<void> => {
    await apiService.savePasswordPolicy({ ...form });
};

// ---- License Info ----

export interface LicenseInfoForm {
    licensesUsed: string;
    ascAccounts: string;
    licensesPurchased: string;
    expirationDate: string;
}

export const licenseInfoEmptyForm: LicenseInfoForm = { licensesUsed: '', ascAccounts: '', licensesPurchased: '', expirationDate: '' };

export const licenseInfoInitialData = (info: LicenseInfoResponse | null): LicenseInfoForm | null =>
    info ? {
        licensesUsed: info.LicensesUsed?.toString() ?? '',
        ascAccounts: info.AscAccounts?.toString() ?? '',
        licensesPurchased: info.LicensesPurchased?.toString() ?? '',
        expirationDate: info.ExpirationDate ?? '',
    } : null;

export const licenseInfoFields: SettingsFieldConfig<LicenseInfoForm>[] = [
    { key: 'licensesUsed', label: 'Licenses Used', type: 'readonly' },
    { key: 'ascAccounts', label: 'ASC Group Accounts', type: 'readonly' },
    {
        key: 'licensesPurchased', label: 'Licenses Purchased', type: 'number',
        validate: v => {
            if (!String(v).trim()) return 'Number of Licenses Purchased is required.';
            if (isNaN(parseInt(String(v), 10))) return 'Must be a valid integer.';
            return undefined;
        },
    },
    { key: 'expirationDate', label: 'Expiration Date', type: 'text' },
];

export const saveLicenseInfo = async (form: LicenseInfoForm): Promise<void> => {
    await apiService.saveLicenseInfo({ LicensesPurchased: form.licensesPurchased, ExpirationDate: form.expirationDate || undefined });
};

// ---- Offline App Settings ----

export interface OfflineAppSettingsForm {
    mobileDownloadActive: boolean;
    mobileDevActive: boolean;
}

export const offlineAppSettingsEmptyForm: OfflineAppSettingsForm = { mobileDownloadActive: false, mobileDevActive: false };

export const offlineAppSettingsInitialData = (s: OfflineAppSettingsResponse | null): OfflineAppSettingsForm | null =>
    s ? { mobileDownloadActive: s.MobileDownloadActive, mobileDevActive: s.MobileDevActive } : null;

export const offlineAppSettingsFields = (isSuperAdmin: boolean): SettingsFieldConfig<OfflineAppSettingsForm>[] => [
    { key: 'mobileDownloadActive', label: 'Offline Inspection App Download', type: 'toggle' },
    { key: 'mobileDevActive', label: 'Offline Inspection App Dev Mode', type: 'toggle', visible: () => isSuperAdmin },
];

export const saveOfflineAppSettings = async (form: OfflineAppSettingsForm): Promise<void> => {
    await apiService.saveOfflineAppSettings({ MobileDownloadActive: form.mobileDownloadActive, MobileDevActive: form.mobileDevActive });
};

// ---- Sign In Page Message ----

export interface SignInMessageForm {
    message: string;
}

export const signInMessageEmptyForm: SignInMessageForm = { message: '' };

export const signInMessageInitialData = (p: EnterprisePreferencesResponse | null): SignInMessageForm | null =>
    p ? { message: p.SignInMessage ?? '' } : null;

export const signInMessageFields: SettingsFieldConfig<SignInMessageForm>[] = [
    { key: 'message', label: 'Message shown below the sign-in area', type: 'textarea' },
];

export const saveSignInMessage = async (form: SignInMessageForm): Promise<void> => {
    await apiService.saveSignInMessage({ message: form.message });
};
